import React, { useMemo } from 'react';
import {
  resolveSpeakingTopicLabel,
  type TutorAnalyticsSummary,
  type TrendPoint,
  type DailyEngagementDoc,
  type SpeakingMetricsDoc,
  type TutorSessionDoc,
} from '../../tutor/types/tutorAnalytics';
import type { WritingCheckListItem } from '../../modules/learning/api/writing';
import { TutorTrendChart } from '../../components/ai-tutor/analytics/TutorTrendChart';
import { getAnalyticsInsights } from '../../utils/analyticsInsights';

function truncate(str: string | undefined, max: number): string {
  if (!str || typeof str !== 'string') return '—';
  const s = str.trim();
  return s.length <= max ? s : s.slice(0, max) + '…';
}

export interface AnalyticsReportViewProps {
  tutorData: TutorAnalyticsSummary | null;
  writingItems: WritingCheckListItem[];
  days: number;
  studentName?: string;
}

// Strict A4 Dimensions at 96 DPI
const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

const PdfPage: React.FC<{ children: React.ReactNode, pageNum: number, totalPages: number, dateStr: string, title: string, studentName?: string }> = ({ children, pageNum, totalPages, dateStr, title, studentName }) => (
  <div style={{
    width: `${A4_WIDTH}px`,
    height: `${A4_HEIGHT}px`,
    padding: '40px 50px',
    backgroundColor: '#ffffff',
    boxSizing: 'border-box',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    // FIX: Only add a page break if it is NOT the last page to prevent the blank 5th page
    pageBreakAfter: pageNum < totalPages ? 'always' : 'auto', 
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '2px solid #e5e7eb', paddingBottom: '12px', marginBottom: '24px' }}>
      <div>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, color: '#111827' }}>{title}</h1>
        {studentName && <p style={{ margin: '4px 0 0 0', fontSize: 14, color: '#4b5563', fontWeight: 500 }}>{studentName}</p>}
      </div>
      <div style={{ textAlign: 'right', color: '#6b7280', fontSize: 12 }}>
        <p style={{ margin: 0 }}>Generated: {dateStr}</p>
        <p style={{ margin: '4px 0 0 0', fontWeight: 500 }}>Page {pageNum} of {totalPages}</p>
      </div>
    </div>

    <div style={{ flex: 1 }}>
      {children}
    </div>

    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '24px', textAlign: 'center', color: '#9ca3af', fontSize: 10, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
      Tamil Tutor • AI Learning Platform Analytics
    </div>
  </div>
);

export const AnalyticsReportView: React.FC<AnalyticsReportViewProps> = ({
  tutorData,
  writingItems,
  days,
  studentName,
}) => {
  const generatedAt = new Date().toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  const periodLabel = `Last ${days} days`;

  // Derived data (same as AnalyticsCharts)
  const dailyEngagementBars = useMemo(() => {
    const d = tutorData?.daily_engagement ?? [];
    return d.slice().sort((a, b) => (a.date < b.date ? -1 : 1)).map((x: DailyEngagementDoc) => ({
      label: new Date(x.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      sessions: x.sessions ?? 0,
    }));
  }, [tutorData]);

  const speakingAverages = useMemo(() => {
    const sessions = tutorData?.speaking_sessions ?? [];
    if (!sessions.length) return [];
    const keys = ['pronunciation', 'fluency', 'grammar', 'vocabulary', 'overall'] as const;
    return keys.map(key => {
      const sum = sessions.reduce((s, x) => s + ((x.scores as Record<string, number>)?.[key] ?? 0), 0);
      return { label: key.charAt(0).toUpperCase() + key.slice(1), value: Math.round((sum / sessions.length) * 10) / 10 };
    });
  }, [tutorData]);

  const writingScorePerCheck = useMemo(() => {
    return writingItems.filter(i => i.created_at).slice().sort((a, b) => (a.created_at! < b.created_at! ? -1 : 1)).slice(-10).map(item => ({
      label: new Date(item.created_at!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      value: Math.max(0, Math.min(100, item.derived_metadata?.overall_score ?? item.result?.rubric?.overallScore ?? 0)),
    }));
  }, [writingItems]);

  const writingRubricAverages = useMemo(() => {
    if (!writingItems.length) return [];
    const config = [
      { key: 'grammar_score', rubricKey: 'grammar', label: 'Grammar' },
      { key: 'clarity_score', rubricKey: 'clarity', label: 'Clarity' },
      { key: 'vocabulary_score', rubricKey: 'vocabulary', label: 'Vocabulary' },
      { key: 'overall_score', rubricKey: 'overallScore', label: 'Overall' },
    ] as const;
    return config.map(({ key, rubricKey, label }) => {
      const sum = writingItems.reduce((s, item) => s + (typeof (item.derived_metadata?.[key] ?? (item.result?.rubric as any)?.[rubricKey]) === 'number' ? (item.derived_metadata?.[key] ?? (item.result?.rubric as any)?.[rubricKey]) : 0), 0);
      return { label, value: Math.round((sum / writingItems.length) * 10) / 10 };
    });
  }, [writingItems]);

  const writingSuggestionsPerCheck = useMemo(() => {
    return writingItems.filter(i => i.created_at).slice().sort((a, b) => (a.created_at! < b.created_at! ? -1 : 1)).slice(-10).map(item => ({
      label: new Date(item.created_at!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      value: item.derived_metadata?.suggestion_count ?? 0,
    }));
  }, [writingItems]);

  const trend: TrendPoint[] = tutorData?.trend ?? [];
  const totalWritingSuggestions = useMemo(() => writingItems.reduce((s, i) => s + (i.derived_metadata?.suggestion_count ?? 0), 0), [writingItems]);
  
  const latestWritingScore = useMemo(() => {
    const withDate = writingItems.filter(i => i.created_at).sort((a, b) => (a.created_at! > b.created_at! ? -1 : 1));
    return withDate[0] ? Math.max(0, Math.min(100, withDate[0].derived_metadata?.overall_score ?? withDate[0].result?.rubric?.overallScore ?? 0)) : null;
  }, [writingItems]);

  const avgWritingScore = useMemo(() => {
    if (!writingItems.length) return null;
    const sum = writingItems.reduce((s, item) => s + (typeof (item.derived_metadata?.overall_score ?? item.result?.rubric?.overallScore) === 'number' ? (item.derived_metadata?.overall_score ?? item.result?.rubric?.overallScore ?? 0) : 0), 0);
    return Math.round((sum / writingItems.length) * 10) / 10;
  }, [writingItems]);

  const insightInputs = useMemo(() => {
    const speaking: any = {};
    speakingAverages.forEach(({ label, value }) => { speaking[label.toLowerCase()] = value; });
    const writing: any = {};
    writingRubricAverages.forEach(({ label, value }) => { writing[label.toLowerCase()] = value; });
    return { speakingAverages: speaking, writingAverages: writing, totalWritingSuggestions, writingChecksCount: writingItems.length, sessionsCount: tutorData?.sessions_count ?? 0 };
  }, [speakingAverages, writingRubricAverages, totalWritingSuggestions, writingItems.length, tutorData?.sessions_count]);
  
  const insights = getAnalyticsInsights(insightInputs);
  const recentSessions = (tutorData?.session_history ?? []).slice(-12).reverse();
  const recentWriting = writingItems.filter(i => i.created_at).sort((a, b) => (a.created_at! > b.created_at! ? -1 : 1)).slice(0, 8);

  const TOTAL_PAGES = 4;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, margin: 0, padding: 0, fontFamily: 'system-ui, "Noto Sans Tamil", sans-serif' }}>
      
      {/* ================= PAGE 1 ================= */}
      <PdfPage pageNum={1} totalPages={TOTAL_PAGES} dateStr={generatedAt} title="Student Analytics Report" studentName={studentName}>
        
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px 0', color: '#1f2937' }}>Executive Summary ({periodLabel})</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, fontSize: 12, marginBottom: 32 }}>
          <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}><strong style={{color:'#64748b', display:'block', marginBottom:'4px'}}>Tutor time</strong><span style={{fontSize: '20px', fontWeight: 700, color: '#0f172a'}}>{tutorData ? `${Math.floor(tutorData.total_tutor_minutes / 60)}h ${tutorData.total_tutor_minutes % 60}m` : '—'}</span></div>
          <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}><strong style={{color:'#64748b', display:'block', marginBottom:'4px'}}>Total sessions</strong><span style={{fontSize: '20px', fontWeight: 700, color: '#0f172a'}}>{tutorData?.sessions_count ?? '—'}</span></div>
          <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}><strong style={{color:'#64748b', display:'block', marginBottom:'4px'}}>Latest spk. level</strong><span style={{fontSize: '20px', fontWeight: 700, color: '#10b981'}}>{tutorData?.latest_speaking_level ?? '—'}</span></div>
          <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}><strong style={{color:'#64748b', display:'block', marginBottom:'4px'}}>Speaking score</strong><span style={{fontSize: '20px', fontWeight: 700, color: '#10b981'}}>{tutorData?.latest_overall_score != null ? `${tutorData.latest_overall_score}%` : '—'}</span></div>
          <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}><strong style={{color:'#64748b', display:'block', marginBottom:'4px'}}>Writing checks</strong><span style={{fontSize: '20px', fontWeight: 700, color: '#8b5cf6'}}>{writingItems.length}</span></div>
          <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}><strong style={{color:'#64748b', display:'block', marginBottom:'4px'}}>Latest writing</strong><span style={{fontSize: '20px', fontWeight: 700, color: '#8b5cf6'}}>{latestWritingScore != null ? `${latestWritingScore}%` : '—'}</span></div>
          <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}><strong style={{color:'#64748b', display:'block', marginBottom:'4px'}}>Avg writing</strong><span style={{fontSize: '20px', fontWeight: 700, color: '#8b5cf6'}}>{avgWritingScore != null ? `${avgWritingScore}%` : '—'}</span></div>
          <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}><strong style={{color:'#64748b', display:'block', marginBottom:'4px'}}>Suggestions</strong><span style={{fontSize: '20px', fontWeight: 700, color: '#f59e0b'}}>{totalWritingSuggestions}</span></div>
        </div>

        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px 0', color: '#1f2937' }}>Tutor Analytics — Charts</h2>
        
        {/* FIX: Height increased to 380px so the dark trend chart does not spill down over the bottom charts */}
        <div style={{ width: '100%', height: '380px', marginBottom: '40px' }}>
          <TutorTrendChart trend={trend} />
        </div>

        <div style={{ display: 'flex', gap: 24 }}>
          {dailyEngagementBars.length > 0 && (
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', color: '#374151' }}>Daily engagement (sessions)</h3>
              <svg width="100%" viewBox="0 0 320 200" style={{ display: 'block', overflow: 'visible' }}>
                {dailyEngagementBars.slice(-14).map((d, i) => {
                  const maxS = Math.max(...dailyEngagementBars.slice(-14).map(x => x.sessions), 1);
                  const h = (d.sessions / maxS) * 140;
                  const x = 10 + i * 21;
                  const y = 160 - h;
                  return (
                    <g key={i}>
                      <rect x={x} y={y} width={14} height={h} rx={3} fill="#3b82f6" />
                      <text x={x + 7} y={172} textAnchor="end" transform={`rotate(-45 ${x + 7} 172)`} fontSize="10" fill="#6b7280">{d.label}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          )}
          
          {speakingAverages.length > 0 && (
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', color: '#374151' }}>Speaking score breakdown</h3>
              <svg width="100%" viewBox="0 0 320 200" style={{ display: 'block' }}>
                {speakingAverages.map((d, i) => {
                  const w = (d.value / 100) * 170;
                  const y = 10 + i * 30;
                  return (
                    <g key={i}>
                      <text x={0} y={y + 12} fontSize="11" fill="#4b5563" fontWeight="500">{d.label}</text>
                      <rect x={90} y={y} width={w} height={16} rx={3} fill="#10b981" />
                      <text x={96 + w} y={y + 12} fontSize="11" fill="#1f2937" fontWeight="600">{d.value}%</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          )}
        </div>
      </PdfPage>

      {/* ================= PAGE 2 ================= */}
      <PdfPage pageNum={2} totalPages={TOTAL_PAGES} dateStr={generatedAt} title="Writing Analytics & Insights" studentName={studentName}>
        
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 24px 0', color: '#1f2937' }}>Writing Analytics — Charts</h2>
        
        <div style={{ display: 'flex', gap: 24, marginBottom: 32 }}>
          {writingScorePerCheck.length > 0 && (
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', color: '#374151' }}>Writing score per check</h3>
              <svg width="100%" viewBox="0 0 320 200" style={{ display: 'block', overflow: 'visible' }}>
                {writingScorePerCheck.map((d, i) => {
                  const h = (d.value / 100) * 140;
                  const x = 10 + i * 28;
                  const y = 160 - h;
                  return (
                    <g key={i}>
                      <rect x={x} y={y} width={16} height={h} rx={3} fill="#8b5cf6" />
                      <text x={x + 8} y={172} textAnchor="end" transform={`rotate(-45 ${x + 8} 172)`} fontSize="10" fill="#6b7280">{d.label}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          )}

          {writingRubricAverages.length > 0 && (
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', color: '#374151' }}>Writing rubric averages</h3>
              <svg width="100%" viewBox="0 0 320 200" style={{ display: 'block' }}>
                {writingRubricAverages.map((d, i) => {
                  const w = (d.value / 100) * 170;
                  const y = 10 + i * 32;
                  return (
                    <g key={i}>
                      <text x={0} y={y + 12} fontSize="11" fill="#4b5563" fontWeight="500">{d.label}</text>
                      <rect x={80} y={y} width={w} height={18} rx={3} fill="#a855f7" />
                      <text x={86 + w} y={y + 13} fontSize="11" fill="#1f2937" fontWeight="600">{d.value}%</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          )}
        </div>

        {writingSuggestionsPerCheck.length > 0 && (
          <div style={{ marginBottom: 40 }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 12px 0', color: '#374151' }}>Suggestions per check (Recent)</h3>
            <svg width="100%" viewBox="0 0 694 220" style={{ display: 'block' }}>
              {writingSuggestionsPerCheck.map((d, i) => {
                const maxV = Math.max(...writingSuggestionsPerCheck.map(x => x.value), 1);
                const w = (d.value / maxV) * 550;
                const y = 10 + i * 20;
                return (
                  <g key={i}>
                    <text x={0} y={y + 10} fontSize="10" fill="#4b5563">{d.label}</text>
                    <rect x={60} y={y} width={w} height={12} rx={2} fill="#f59e0b" />
                    <text x={66 + w} y={y + 10} fontSize="10" fill="#1f2937" fontWeight="600">{d.value}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        )}
      </PdfPage>

      {/* ================= PAGE 3 ================= */}
      <PdfPage pageNum={3} totalPages={TOTAL_PAGES} dateStr={generatedAt} title="Feedback & Improvement Plan" studentName={studentName}>
        
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 12px 0', color: '#1f2937', paddingBottom: '6px', borderBottom: '2px solid #f1f5f9' }}>Recent Speaking Feedback</h2>
        {(tutorData?.speaking_sessions ?? []).length > 0 ? (
          <div style={{ marginBottom: 32 }}>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, lineHeight: '1.6', color: '#334155' }}>
              {(tutorData!.speaking_sessions as SpeakingMetricsDoc[]).slice(0, 4).map((s, i) => {
                const topic = resolveSpeakingTopicLabel(s, tutorData?.session_history ?? []);
                return (
                  <li key={i} style={{ marginBottom: '12px' }}>
                    <strong style={{ color: '#0f172a' }}>{new Date(s.created_at).toLocaleDateString()}</strong>
                    {' '}(Overall: {s.scores?.overall ?? '—'}%){' '}
                    <span style={{ color: '#475569' }}>Topic: {topic || '—'}</span>
                    <br /> {truncate(s.feedback_ta, 150)}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 32 }}>No recent speaking feedback available.</p>
        )}

        <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 12px 0', color: '#1f2937', paddingBottom: '6px', borderBottom: '2px solid #f1f5f9' }}>Recent Writing Feedback</h2>
        {recentWriting.length > 0 ? (
          <div style={{ marginBottom: 32 }}>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, lineHeight: '1.6', color: '#334155' }}>
              {recentWriting.slice(0, 4).map((item, i) => (
                <li key={i} style={{ marginBottom: '12px' }}>
                  <strong style={{ color: '#0f172a' }}>{item.created_at ? new Date(item.created_at).toLocaleDateString() : '—'}</strong> (Score: {item.derived_metadata?.overall_score ?? item.result?.rubric?.overallScore ?? '—'}%) <br/> {truncate(item.result?.rubric?.feedback, 150)}
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 32 }}>No recent writing feedback available.</p>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, backgroundColor: '#f8fafc', padding: '24px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px 0', color: '#b91c1c' }}>Areas to Improve</h2>
            {insights.areasToImprove.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: '1.5', color: '#334155' }}>
                {insights.areasToImprove.map((a, i) => (
                  <li key={i} style={{ marginBottom: '6px' }}>{a}</li>
                ))}
              </ul>
            ) : (
              <p style={{ fontSize: 12, margin: 0, color: '#64748b' }}>No specific areas identified. Keep practicing!</p>
            )}
          </div>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px 0', color: '#15803d' }}>Recommended Next Steps</h2>
            <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, lineHeight: '1.5', color: '#334155' }}>
              {insights.recommendedNextSteps.map((s, i) => (
                <li key={i} style={{ marginBottom: '6px' }}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </PdfPage>

      {/* ================= PAGE 4 ================= */}
      <PdfPage pageNum={4} totalPages={TOTAL_PAGES} dateStr={generatedAt} title="Appendix — Raw Data" studentName={studentName}>
        
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px 0', color: '#1f2937' }}>Session History (Last 12)</h2>
        {recentSessions.length > 0 ? (
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', textAlign: 'left', marginBottom: '32px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 10px', fontWeight: 600, color: '#334155' }}>Date</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, color: '#334155' }}>Mode</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, color: '#334155' }}>Tutor type</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, color: '#334155', textAlign: 'right' }}>Duration</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, color: '#334155' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentSessions.map((s, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ padding: '8px 10px' }}>{new Date(s.started_at).toLocaleString()}</td>
                  <td style={{ padding: '8px 10px' }}>{s.mode}</td>
                  <td style={{ padding: '8px 10px' }}>{s.tutor_type}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{Math.round(s.duration_seconds)}s</td>
                  <td style={{ padding: '8px 10px' }}>{s.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ fontSize: 11, color: '#64748b', marginBottom: '32px' }}>No session history in this period.</p>
        )}
        
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: '0 0 12px 0', color: '#1f2937' }}>Writing Checks (Last 8)</h2>
        {recentWriting.length > 0 ? (
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', textAlign: 'left' }}>
            <thead>
              <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
                <th style={{ padding: '8px 10px', fontWeight: 600, color: '#334155' }}>Date</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, color: '#334155', textAlign: 'right' }}>Score</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, color: '#334155', textAlign: 'right' }}>Suggestions</th>
                <th style={{ padding: '8px 10px', fontWeight: 600, color: '#334155' }}>Feedback Snippet</th>
              </tr>
            </thead>
            <tbody>
              {recentWriting.map((item, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{item.created_at ? new Date(item.created_at).toLocaleString() : '—'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#8b5cf6' }}>{item.derived_metadata?.overall_score ?? item.result?.rubric?.overallScore ?? '—'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>{item.derived_metadata?.suggestion_count ?? 0}</td>
                  <td style={{ padding: '8px 10px', color: '#4b5563' }}>{truncate(item.result?.rubric?.feedback, 60)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p style={{ fontSize: 11, color: '#64748b' }}>No writing checks in this period.</p>
        )}
      </PdfPage>

    </div>
  );
};
